import React, { useEffect, useState, useRef } from "react";
import { useQuery } from "react-query";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Divider,
  Button,
  Collapse,
} from "@mui/material";
import {
  People as PeopleIcon,
  AccountBalanceWallet as WalletIcon,
  Receipt as ReceiptIcon,
  LocalOffer as CouponIcon,
  Work as WorkIcon,
  Store as StoreIcon,
  TrendingUp as TrendingUpIcon,
  Settings as SettingsIcon,
  ReceiptLong as ReceiptLongIcon,
  Visibility as VisibilityIcon,
  QuestionAnswer as QuestionAnswerIcon,
  ShoppingCartCheckout as ShoppingCartCheckoutIcon,
  BusinessCenter as BusinessCenterIcon,
  Today as TodayIcon,
  CalendarToday as CalendarTodayIcon,
  DateRange as DateRangeIcon,
  CurrencyRupee as CurrencyRupeeIcon,
  PendingActions as PendingActionsIcon,
  Savings as SavingsIcon,
  Assessment as AssessmentIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CloudDownload as CloudDownloadIcon,
  VerifiedUser as VerifiedUserIcon,
  CalendarMonth as CalendarMonthIcon,
  DashboardCustomize as DashboardCustomizeIcon,
  TableChart as TableChartIcon,
  WhatsApp as WhatsAppIcon,
  PersonAdd as PersonAddIcon,
  School as SchoolIcon,
  Assignment as AssignmentIcon,
  LibraryBooks as LibraryBooksIcon,
  Forum as ForumIcon,
  Inventory2Outlined as Inventory2OutlinedIcon,
  PhoneAndroid as PhoneAndroidIcon,
  GridView as GridViewIcon,
  HighQuality as HighQualityIcon,
  Tune as TuneIcon,
  WorkspacePremium as WorkspacePremiumIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../contexts/AuthContext";
import { expandLegacyOperatorPermissions } from "../../utils/expandLegacyOperatorPermissions";
import { billsApi } from "../../services/smartBillApi";
import { pathnameAllowedByFranchiseFeatures } from "../../config/franchiseeFeatures";
import { isLocalInternalHost } from "../../utils/localInternalHost";
import {
  ROLES_NEED_DEFAULT_UPI_SETUP,
  fetchDefaultUpiId,
  isDefaultUpiConfigured,
} from "../../services/adminSettingsService";

// Use env var if set, otherwise use relative URL (works for same-domain backend)
const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  // In production (services.knls.in), use relative URL
  if (
    typeof window !== "undefined" &&
    (window.location.hostname.includes("ondigitalocean.app") ||
      window.location.hostname.includes("services.knls.in"))
  ) {
    return "/api";
  }
  // Development fallback
  return "http://localhost:3000/api";
};

const API_BASE_URL = getApiBaseUrl();

const GRID_SPACING = { xs: 2, md: 3 };

const gradientCardContentSx = {
  display: "flex",
  gap: { xs: 1.5, sm: 2 },
  alignItems: "center",
  p: { xs: 2, sm: 2.5 },
  "&:last-child": { pb: { xs: 2, sm: 2.5 } },
};

const gradientCardIconSx = {
  width: { xs: 48, sm: 56 },
  height: { xs: 48, sm: 56 },
  borderRadius: "50%",
  backgroundColor: "rgba(255,255,255,0.2)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

const gradientCardTextSx = {
  minWidth: 0,
  flex: 1,
};

const billStatRowSx = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: 1.5,
};

const billStatTextSx = {
  minWidth: 0,
  flex: 1,
};

const sectionTitleSx = {
  fontSize: { xs: "1.25rem", sm: "1.5rem" },
};

const StatCard = ({ title, value, icon, color, change }) => (
  <Card sx={{ height: "100%" }}>
    <CardContent sx={{ p: { xs: 2, sm: 2.5 }, "&:last-child": { pb: { xs: 2, sm: 2.5 } } }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 1.5,
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography color="text.secondary" gutterBottom variant="body2">
            {title}
          </Typography>
          <Typography
            variant="h4"
            component="div"
            fontWeight="bold"
            sx={{ fontSize: { xs: "1.5rem", sm: "2rem" }, wordBreak: "break-word" }}
          >
            {value}
          </Typography>
          {change != null && change !== 0 && (
            <Typography
              variant="body2"
              color={change > 0 ? "success.main" : "error.main"}
              sx={{ mt: 1 }}
            >
              <TrendingUpIcon
                sx={{ fontSize: 16, verticalAlign: "middle", mr: 0.5 }}
              />
              {change > 0 ? "+" : ""}
              {change}% from last month
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            bgcolor: `${color}.light`,
            color: `${color}.dark`,
            borderRadius: 2,
            p: { xs: 1, sm: 1.5 },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            "& .MuiSvgIcon-root": { fontSize: { xs: 32, sm: 40 } },
          }}
        >
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user?.role || !ROLES_NEED_DEFAULT_UPI_SETUP.has(user.role)) return;
    let cancelled = false;
    (async () => {
      try {
        const upi = await fetchDefaultUpiId();
        if (cancelled) return;
        if (!isDefaultUpiConfigured(upi)) {
          navigate("/bills/admin-controls", { replace: true });
        }
      } catch {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.role, navigate]);
  const [billStats, setBillStats] = useState({
    today: { count: 0, total: 0 },
    weekly: { count: 0, total: 0 },
    monthly: { count: 0, total: 0 },
    loading: true,
  });
  const [billOverview, setBillOverview] = useState({
    totalBills: 0,
    totalCancelled: 0,
    totalRevenue: 0,
    pendingAmount: 0,
    couponSavings: 0,
    averageBill: 0,
  });
  const [billsOverviewExpanded, setBillsOverviewExpanded] = useState(false);

  const formatCurrency = (value) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(value) || 0);

  const formatNumber = (value) =>
    new Intl.NumberFormat("en-IN").format(Number(value) || 0);

  // Helper to check if a card/route should be visible based on user role and features
  const canAccessRoute = (path) => {
    if (!user) return false;
    if (path === "/bills/manage" && !isLocalInternalHost()) return false;
    if (user.role === "app_operator") {
      const permissions = expandLegacyOperatorPermissions(
        Array.isArray(user.permissions) ? user.permissions : [],
      );
      const routePermissions = {
        "/bills/create": ["bills_creation"],
        "/bills/cart-model": ["bills_creation"],
        "/bills/view": ["bills_view"],
        "/bills/manage": ["old_site_bills"],
        "/bills/pending-works": ["pending_works"],
        "/bills/advance-bills": ["bills_view"],
        "/bills/enquiries": ["enquiries"],
        "/bills/enquiry-form": ["enquiries"],
        "/bills/operator-calls": ["operator_call_leads"],
        "/application-chat": ["enquiries"],
        "/customer/search": ["customer_search"],
        "/customer/dashboard": ["customer_dashboard"],
        "/dashboard/students/attendance": ["digital_attendance"],
        "/business-admin/register": ["vyapar_assisted_registration"],
      };
      const required = routePermissions[path];
      if (!required) return false;
      return required.some((perm) => permissions.includes(perm));
    }
    // For franchisee_admin and net_center_admin, check features
    if (user.role === "franchisee_admin" || user.role === "net_center_admin") {
      return pathnameAllowedByFranchiseFeatures(path, user.features);
    }
    // For other roles, show all cards
    return true;
  };

  const lastBillStatsFetchRef = useRef(0);
  const isFetchingBillStatsRef = useRef(false);
  const BILL_STATS_STALE_MS = 2 * 60 * 1000;

  // Fetch and calculate bill statistics
  useEffect(() => {
    const shouldFetchBillStats = () => {
      const now = Date.now();
      return now - lastBillStatsFetchRef.current > BILL_STATS_STALE_MS;
    };

    const fetchBillStats = async (force = false) => {
      if (isFetchingBillStatsRef.current) return;
      if (!force && !shouldFetchBillStats()) return;

      try {
        isFetchingBillStatsRef.current = true;
        lastBillStatsFetchRef.current = Date.now();
        setBillStats((prev) => ({ ...prev, loading: true }));

        // Get all bills
        const response = await billsApi.getAll({ limit: 10000 });
        const bills = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : [];

        const now = new Date();
        const today = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate(),
        );
        const weekStart = new Date(today);
        weekStart.setDate(weekStart.getDate() - weekStart.getDay()); // Start of week (Sunday)
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

        let todayCount = 0;
        let todayTotal = 0;
        let weeklyCount = 0;
        let weeklyTotal = 0;
        let monthlyCount = 0;
        let monthlyTotal = 0;

        bills.forEach((bill) => {
          const isAdvance = !!(bill.is_advance ?? bill.isAdvance);
          const salesCountedAt =
            bill.sales_counted_at ?? bill.salesCountedAt ?? null;
          // Advance amounts enter today/counter sales only after work completed.
          if (isAdvance && !salesCountedAt) return;

          const billDate = new Date(
            salesCountedAt ||
              bill.bill_date ||
              bill.billDate ||
              bill.created_at ||
              bill.createdAt,
          );

          // Calculate service charges (excluding challana)
          // Formula: service charges = (price * quantity) - challana
          // Or: service charges = total - (challana * quantity) if total includes challana
          let serviceCharges = 0;
          if (bill.services && Array.isArray(bill.services)) {
            bill.services.forEach((service) => {
              const quantity = Number(service.quantity || 1);
              const challana = Number(
                service.challana || service.challana_amount || 0,
              );
              const servicePrice = Number(
                service.price || service.service_price || 0,
              );
              const serviceTotal = Number(service.total || 0);

              if (servicePrice > 0) {
                // Calculate from price: (price * quantity) - challana
                // Note: challana is usually a one-time fee, not multiplied by quantity
                serviceCharges += servicePrice * quantity - challana;
              } else if (serviceTotal > 0) {
                // If only total is available, subtract challana (assuming challana is not multiplied)
                serviceCharges += serviceTotal - challana;
              }
            });
          } else if (bill.total_amount || bill.totalAmount) {
            // Fallback: use total amount if services breakdown not available
            // Note: This might include challana, but we don't have breakdown to exclude it
            serviceCharges = Number(bill.total_amount || bill.totalAmount || 0);
          }

          // Today
          if (billDate >= today) {
            todayCount++;
            todayTotal += serviceCharges;
          }

          // This week
          if (billDate >= weekStart) {
            weeklyCount++;
            weeklyTotal += serviceCharges;
          }

          // This month
          if (billDate >= monthStart) {
            monthlyCount++;
            monthlyTotal += serviceCharges;
          }
        });

        setBillStats({
          today: { count: todayCount, total: todayTotal },
          weekly: { count: weeklyCount, total: weeklyTotal },
          monthly: { count: monthlyCount, total: monthlyTotal },
          loading: false,
        });

        const serverSummary = response?.summary || {};
        let statusCounts = serverSummary.statusCounts ||
          serverSummary.status_counts || {
            under_processing: 0,
            pending: 0,
            completed: 0,
            cancelled: 0,
          };
        if (typeof statusCounts === "string") {
          try {
            statusCounts = JSON.parse(statusCounts);
          } catch (err) {
            statusCounts = {
              under_processing: 0,
              pending: 0,
              completed: 0,
              cancelled: 0,
            };
          }
        }

        const fallbackRevenue = bills.reduce(
          (sum, bill) =>
            sum +
            Number(bill.total_amount ?? bill.totalAmount ?? bill.amount ?? 0),
          0,
        );
        const fallbackPending = bills.reduce(
          (sum, bill) => sum + Number(bill.due_amount ?? bill.dueAmount ?? 0),
          0,
        );
        const fallbackCancelled = bills.filter(
          (bill) =>
            (bill.payment_status || bill.status || "").toLowerCase() ===
              "cancelled" ||
            (bill.work_status || bill.workStatus || "").toLowerCase() ===
              "cancelled",
        ).length;

        const totalBills = Number(
          serverSummary.totalBills ??
            serverSummary.total_bills ??
            bills.length ??
            0,
        );
        const totalRevenue = Number(
          serverSummary.totalRevenue ??
            serverSummary.total_revenue ??
            fallbackRevenue,
        );
        const pendingAmount = Number(
          serverSummary.pendingAmount ??
            serverSummary.pending_amount ??
            fallbackPending,
        );
        const totalCancelled = Number(
          serverSummary.totalCancelled ??
            serverSummary.total_cancelled ??
            fallbackCancelled,
        );
        const discountAmount = Number(
          serverSummary.discountAmount ?? serverSummary.discount_amount ?? 0,
        );
        const couponDiscount = Number(
          serverSummary.couponDiscount ?? serverSummary.coupon_discount ?? 0,
        );
        const couponSavings = discountAmount + couponDiscount;
        const averageBill = totalBills > 0 ? totalRevenue / totalBills : 0;

        setBillOverview({
          totalBills,
          totalCancelled,
          totalRevenue,
          pendingAmount,
          couponSavings,
          averageBill,
          statusCounts,
        });
      } catch (error) {
        console.error("Error fetching bill stats:", error);
        setBillStats((prev) => ({ ...prev, loading: false }));
      } finally {
        isFetchingBillStatsRef.current = false;
      }
    };

    const handleVisibilityOrFocus = () => {
      if (document.visibilityState === "visible") {
        fetchBillStats();
      }
    };

    fetchBillStats(true);
    window.addEventListener("focus", handleVisibilityOrFocus);
    document.addEventListener("visibilitychange", handleVisibilityOrFocus);

    return () => {
      window.removeEventListener("focus", handleVisibilityOrFocus);
      document.removeEventListener("visibilitychange", handleVisibilityOrFocus);
    };
  }, []);

  const {
    data: stats,
    isLoading,
    error,
  } = useQuery(
    "dashboard-stats",
    async () => {
      try {
        const token = localStorage.getItem("admin_token") || localStorage.getItem("token") || sessionStorage.getItem("admin_token") || sessionStorage.getItem("token");
        const response = await axios.get(`${API_BASE_URL}/dashboard/stats`, {
          headers: token ? {
            Authorization: `Bearer ${token}`,
          } : {},
        });
        return response.data;
      } catch (err) {
        console.error("Dashboard stats API error:", err);
        // Return default stats structure on error
        return {
          users: { total: 0, change: 0 },
          shops: { total: 0, change: 0 },
          bills: { total: 0, change: 0 },
          coupons: { total: 0, change: 0 },
          jobAlerts: { total: 0, change: 0 },
          wallet: { total: 0, change: 0 },
        };
      }
    },
    {
      staleTime: 2 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      retry: 1,
      retryDelay: 1000,
    },
  );

  // Always show content, even if loading or error
  const displayStats = stats || {
    users: { total: 0, change: 0 },
    shops: { total: 0, change: 0 },
    bills: { total: 0, change: 0 },
    coupons: { total: 0, change: 0 },
    jobAlerts: { total: 0, change: 0 },
    wallet: { total: 0, change: 0 },
  };

  const statCards = [
    {
      title: "Total Users",
      value: displayStats?.users?.total || 0,
      icon: <PeopleIcon sx={{ fontSize: 40 }} />,
      color: "primary",
      change: displayStats?.users?.change || 0,
    },
    {
      title: "Active Shops",
      value: displayStats?.shops?.total || 0,
      icon: <StoreIcon sx={{ fontSize: 40 }} />,
      color: "success",
      change: displayStats?.shops?.change || 0,
    },
    {
      title: "Total Bills",
      value: displayStats?.bills?.total || 0,
      icon: <ReceiptIcon sx={{ fontSize: 40 }} />,
      color: "info",
      change: displayStats?.bills?.change || 0,
    },
    {
      title: "Active Coupons",
      value: displayStats?.coupons?.total || 0,
      icon: <CouponIcon sx={{ fontSize: 40 }} />,
      color: "warning",
      change: displayStats?.coupons?.change || 0,
    },
    {
      title: "Job Alerts",
      value: displayStats?.jobAlerts?.total || 0,
      icon: <WorkIcon sx={{ fontSize: 40 }} />,
      color: "secondary",
      change: displayStats?.jobAlerts?.change || 0,
    },
    {
      title: "Wallet Balance",
      value: `₹${displayStats?.wallet?.total || 0}`,
      icon: <WalletIcon sx={{ fontSize: 40 }} />,
      color: "error",
      change: displayStats?.wallet?.change || 0,
    },
  ];

  const billOverviewCards = [
    {
      title: "Total Bills",
      value: formatNumber(billOverview.totalBills),
      subtitle: `${formatNumber(billOverview.totalCancelled)} cancelled`,
      icon: <ReceiptIcon sx={{ fontSize: 36 }} />,
      gradient: "linear-gradient(135deg, #6d28d9 0%, #8b5cf6 100%)",
    },
    {
      title: "Total Revenue",
      value: formatCurrency(billOverview.totalRevenue),
      subtitle: "Net collected",
      icon: <CurrencyRupeeIcon sx={{ fontSize: 36 }} />,
      gradient: "linear-gradient(135deg, #059669 0%, #10b981 100%)",
    },
    {
      title: "Pending Amount",
      value: formatCurrency(billOverview.pendingAmount),
      subtitle: "Due + partial bills",
      icon: <PendingActionsIcon sx={{ fontSize: 36 }} />,
      gradient: "linear-gradient(135deg, #c2410c 0%, #f97316 100%)",
    },
    {
      title: "Coupon Savings",
      value: formatCurrency(billOverview.couponSavings),
      subtitle: "Offers + coupons",
      icon: <SavingsIcon sx={{ fontSize: 36 }} />,
      gradient: "linear-gradient(135deg, #2563eb 0%, #38bdf8 100%)",
    },
    {
      title: "Average Bill",
      value: formatCurrency(billOverview.averageBill),
      subtitle: "Across latest range",
      icon: <AssessmentIcon sx={{ fontSize: 36 }} />,
      gradient: "linear-gradient(135deg, #1f2937 0%, #4b5563 100%)",
    },
    {
      title: "Coupon Bills",
      value: formatNumber(displayStats?.couponBills?.total || 0),
      subtitle:
        formatCurrency(displayStats?.couponBills?.revenue || 0) + " revenue",
      icon: <CouponIcon sx={{ fontSize: 36 }} />,
      gradient: "linear-gradient(135deg, #dc2626 0%, #f87171 100%)",
    },
  ];

  // Check if user is app_operator
  const isOperator = user?.role === "app_operator";
  const hasPendingWorksPermission =
    isOperator &&
    Array.isArray(user?.permissions) &&
    user.permissions.includes("pending_works");

  // Debug logging (remove in production)
  if (isOperator) {
    console.log("Operator detected:", {
      role: user?.role,
      permissions: user?.permissions,
    });
  }

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        overflowX: "hidden",
        boxSizing: "border-box",
      }}
    >
      <Typography
        variant="h4"
        component="h1"
        gutterBottom
        fontWeight="bold"
        sx={{ fontSize: { xs: "1.5rem", sm: "2rem" } }}
      >
        Dashboard
      </Typography>
      <Typography
        variant="body1"
        color="text.secondary"
        sx={{ mb: { xs: 2.5, md: 4 }, fontSize: { xs: "0.875rem", sm: "1rem" } }}
      >
        Here's what's happening with your platform today.
      </Typography>

      {error && !isOperator && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Unable to fetch live statistics. Showing cached or default values.
        </Alert>
      )}

      {/* Show statistics cards only for non-operators */}
      {!isOperator && (
        <Grid container spacing={GRID_SPACING}>
          {statCards.map((card, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <StatCard {...card} />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Bills Overview: collapsed by default — expand only when admin needs sensitive metrics */}
      {!isOperator && (
        <Box sx={{ mt: { xs: 3, md: 4 } }}>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 2,
              mb: billsOverviewExpanded ? 3 : 0,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography
                variant="h5"
                component="h2"
                gutterBottom
                fontWeight="bold"
                sx={{ mb: 0.5, ...sectionTitleSx }}
              >
                Bills Overview
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {billsOverviewExpanded
                  ? "Snapshot of live bill intelligence metrics."
                  : "Revenue and billing metrics are hidden by default. Expand only when you need to review them."}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setBillsOverviewExpanded((prev) => !prev)}
              endIcon={
                billsOverviewExpanded ? (
                  <ExpandLessIcon />
                ) : (
                  <ExpandMoreIcon />
                )
              }
              aria-expanded={billsOverviewExpanded}
              aria-controls="bills-overview-panel"
              sx={{ width: { xs: "100%", sm: "auto" }, flexShrink: 0 }}
            >
              {billsOverviewExpanded ? "Hide overview" : "Show overview"}
            </Button>
          </Box>
          <Collapse in={billsOverviewExpanded} id="bills-overview-panel">
            <Grid container spacing={GRID_SPACING}>
              {billOverviewCards.map((card) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  lg={billOverviewCards.length >= 4 ? 3 : 4}
                  key={card.title}
                >
                  <Card
                    sx={{
                      background: card.gradient,
                      color: "#fff",
                      borderRadius: 3,
                      boxShadow: "0 16px 40px rgba(15,23,42,0.25)",
                    }}
                  >
                    <CardContent sx={gradientCardContentSx}>
                      <Box sx={gradientCardIconSx}>
                        {card.icon}
                    </Box>
                    <Box sx={gradientCardTextSx}>
                      <Typography
                        variant="overline"
                          sx={{ color: "rgba(255,255,255,0.8)" }}
                        >
                          {card.title}
                        </Typography>
                        <Typography
                          variant="h5"
                          fontWeight="bold"
                          sx={{ mt: 0.5 }}
                        >
                          {card.value}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: "rgba(255,255,255,0.9)" }}
                        >
                          {card.subtitle}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Collapse>
        </Box>
      )}

      {/* Bills cards - Only for operators with pending_works / bills_view */}
      {isOperator && hasPendingWorksPermission && (
        <Box sx={{ mt: { xs: 3, md: 4 } }}>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            fontWeight="bold"
            sx={sectionTitleSx}
          >
            Bills
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            View bills, advance amounts, and pending work updates.
          </Typography>
          <Grid container spacing={GRID_SPACING}>
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  background:
                    "linear-gradient(135deg, #0369a1 0%, #0ea5e9 100%)",
                  color: "#fff",
                  borderRadius: 3,
                  boxShadow: "0 16px 40px rgba(15,23,42,0.25)",
                  cursor: "pointer",
                  "&:hover": {
                    boxShadow: "0 20px 50px rgba(15,23,42,0.35)",
                    transform: "translateY(-4px)",
                  },
                  transition: "all 0.3s",
                }}
                onClick={() => navigate("/bills/view")}
              >
                <CardContent sx={gradientCardContentSx}>
                  <Box sx={gradientCardIconSx}>
                    <VisibilityIcon sx={{ fontSize: 36 }} />
                  </Box>
                  <Box sx={gradientCardTextSx}>
                    <Typography
                      variant="overline"
                      sx={{ color: "rgba(255,255,255,0.8)" }}
                    >
                      View Bills
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" sx={{ mt: 0.5, fontSize: { xs: "1.1rem", sm: "1.5rem" }, wordBreak: "break-word" }}>
                      Bill History
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "rgba(255,255,255,0.9)" }}
                    >
                      Search and manage all bills
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  background:
                    "linear-gradient(135deg, #1d4ed8 0%, #3b82f6 100%)",
                  color: "#fff",
                  borderRadius: 3,
                  boxShadow: "0 16px 40px rgba(15,23,42,0.25)",
                  cursor: "pointer",
                  "&:hover": {
                    boxShadow: "0 20px 50px rgba(15,23,42,0.35)",
                    transform: "translateY(-4px)",
                  },
                  transition: "all 0.3s",
                }}
                onClick={() => navigate("/bills/advance-bills")}
              >
                <CardContent sx={gradientCardContentSx}>
                  <Box sx={gradientCardIconSx}>
                    <WalletIcon sx={{ fontSize: 36 }} />
                  </Box>
                  <Box sx={gradientCardTextSx}>
                    <Typography
                      variant="overline"
                      sx={{ color: "rgba(255,255,255,0.8)" }}
                    >
                      Advance Bills
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" sx={{ mt: 0.5, fontSize: { xs: "1.1rem", sm: "1.5rem" }, wordBreak: "break-word" }}>
                      Advance Amount
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "rgba(255,255,255,0.9)" }}
                    >
                      Bills marked advance — sales after Completed
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  background:
                    "linear-gradient(135deg, #c2410c 0%, #f97316 100%)",
                  color: "#fff",
                  borderRadius: 3,
                  boxShadow: "0 16px 40px rgba(15,23,42,0.25)",
                  cursor: "pointer",
                  "&:hover": {
                    boxShadow: "0 20px 50px rgba(15,23,42,0.35)",
                    transform: "translateY(-4px)",
                  },
                  transition: "all 0.3s",
                }}
                onClick={() => navigate("/bills/pending-works")}
              >
                <CardContent sx={gradientCardContentSx}>
                  <Box sx={gradientCardIconSx}>
                    <PendingActionsIcon sx={{ fontSize: 36 }} />
                    </Box>
                    <Box sx={gradientCardTextSx}>
                      <Typography
                        variant="overline"
                      sx={{ color: "rgba(255,255,255,0.8)" }}
                    >
                      Pending Works
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" sx={{ mt: 0.5, fontSize: { xs: "1.1rem", sm: "1.5rem" }, wordBreak: "break-word" }}>
                      Manage Works
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "rgba(255,255,255,0.9)" }}
                    >
                      Click to view and manage pending works
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Quick Actions - Only for admins */}
      {(user?.role === "app_admin" || user?.role === "super_admin") && (
        <Box sx={{ mt: { xs: 3, md: 4 } }}>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            fontWeight="bold"
            sx={sectionTitleSx}
          >
            Quick Actions
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            All Mitra / Citizen / LMS controls, membership plans, spinner, catalog defaults — open the hub or jump to a card.
          </Typography>
          <Grid container spacing={GRID_SPACING}>
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  background:
                    "linear-gradient(135deg, #0f172a 0%, #334155 100%)",
                  color: "#fff",
                  borderRadius: 3,
                  boxShadow: "0 16px 40px rgba(15,23,42,0.25)",
                  cursor: "pointer",
                  "&:hover": {
                    boxShadow: "0 20px 50px rgba(15,23,42,0.35)",
                    transform: "translateY(-4px)",
                  },
                  transition: "all 0.3s",
                }}
                onClick={() => navigate("/admin-control-screens")}
              >
                <CardContent sx={gradientCardContentSx}>
                  <Box sx={gradientCardIconSx}>
                    <TuneIcon sx={{ fontSize: 36 }} />
                  </Box>
                  <Box sx={gradientCardTextSx}>
                    <Typography
                      variant="overline"
                      sx={{ color: "rgba(255,255,255,0.8)" }}
                    >
                      AA Controls Hub
                    </Typography>
                    <Typography
                      variant="h5"
                      fontWeight="bold"
                      sx={{
                        mt: 0.5,
                        fontSize: { xs: "1.1rem", sm: "1.5rem" },
                        wordBreak: "break-word",
                      }}
                    >
                      Admin Control Screens
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "rgba(255,255,255,0.9)" }}
                    >
                      Mitra, Citizen, spin, boost, ads, membership — one place
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  background:
                    "linear-gradient(135deg, #c2410c 0%, #f97316 100%)",
                  color: "#fff",
                  borderRadius: 3,
                  boxShadow: "0 16px 40px rgba(15,23,42,0.25)",
                  cursor: "pointer",
                  "&:hover": {
                    boxShadow: "0 20px 50px rgba(15,23,42,0.35)",
                    transform: "translateY(-4px)",
                  },
                  transition: "all 0.3s",
                }}
                onClick={() => navigate("/content/membership-plans")}
              >
                <CardContent sx={gradientCardContentSx}>
                  <Box sx={gradientCardIconSx}>
                    <WorkspacePremiumIcon sx={{ fontSize: 36 }} />
                  </Box>
                  <Box sx={gradientCardTextSx}>
                    <Typography
                      variant="overline"
                      sx={{ color: "rgba(255,255,255,0.8)" }}
                    >
                      Loksy Membership
                    </Typography>
                    <Typography
                      variant="h5"
                      fontWeight="bold"
                      sx={{
                        mt: 0.5,
                        fontSize: { xs: "1.1rem", sm: "1.5rem" },
                        wordBreak: "break-word",
                      }}
                    >
                      Membership Plans
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "rgba(255,255,255,0.9)" }}
                    >
                      Loksy Connect / Elite / Prime — names, prices & features
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  background:
                    "linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)",
                  color: "#fff",
                  borderRadius: 3,
                  boxShadow: "0 16px 40px rgba(15,23,42,0.25)",
                  cursor: "pointer",
                  "&:hover": {
                    boxShadow: "0 20px 50px rgba(15,23,42,0.35)",
                    transform: "translateY(-4px)",
                  },
                  transition: "all 0.3s",
                }}
                onClick={() => navigate("/dashboard-cards")}
              >
                <CardContent sx={gradientCardContentSx}>
                  <Box sx={gradientCardIconSx}>
                    <DashboardCustomizeIcon sx={{ fontSize: 36 }} />
                    </Box>
                    <Box sx={gradientCardTextSx}>
                      <Typography
                        variant="overline"
                      sx={{ color: "rgba(255,255,255,0.8)" }}
                    >
                      Dashboard Cards
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" sx={{ mt: 0.5, fontSize: { xs: "1.1rem", sm: "1.5rem" }, wordBreak: "break-word" }}>
                      Control Visibility
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "rgba(255,255,255,0.9)" }}
                    >
                      Show/hide cards on user dashboard
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  background:
                    "linear-gradient(135deg, #047857 0%, #10b981 100%)",
                  color: "#fff",
                  borderRadius: 3,
                  boxShadow: "0 16px 40px rgba(15,23,42,0.25)",
                  cursor: "pointer",
                  "&:hover": {
                    boxShadow: "0 20px 50px rgba(15,23,42,0.35)",
                    transform: "translateY(-4px)",
                  },
                  transition: "all 0.3s",
                }}
                onClick={() => navigate("/mitrascreens")}
              >
                <CardContent sx={gradientCardContentSx}>
                  <Box sx={gradientCardIconSx}>
                    <PhoneAndroidIcon sx={{ fontSize: 36 }} />
                  </Box>
                  <Box sx={gradientCardTextSx}>
                    <Typography
                      variant="overline"
                      sx={{ color: "rgba(255,255,255,0.85)" }}
                    >
                      Locsy Mitra
                    </Typography>
                    <Typography
                      variant="h5"
                      fontWeight="bold"
                      sx={{
                        mt: 0.5,
                        fontSize: { xs: "1.1rem", sm: "1.5rem" },
                        wordBreak: "break-word",
                      }}
                    >
                      Mitra Screens
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "rgba(255,255,255,0.92)" }}
                    >
                      Edit Citizen Interaction & Internal Operations cards (EN/TE/HI)
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  background:
                    "linear-gradient(135deg, #1d4ed8 0%, #3b82f6 100%)",
                  color: "#fff",
                  borderRadius: 3,
                  boxShadow: "0 16px 40px rgba(15,23,42,0.25)",
                  cursor: "pointer",
                  "&:hover": {
                    boxShadow: "0 20px 50px rgba(15,23,42,0.35)",
                    transform: "translateY(-4px)",
                  },
                  transition: "all 0.3s",
                }}
                onClick={() => navigate("/loksyhomescreen_controls")}
              >
                <CardContent sx={gradientCardContentSx}>
                  <Box sx={gradientCardIconSx}>
                    <PhoneAndroidIcon sx={{ fontSize: 36 }} />
                  </Box>
                  <Box sx={gradientCardTextSx}>
                    <Typography
                      variant="overline"
                      sx={{ color: "rgba(255,255,255,0.85)" }}
                    >
                      Locsy Citizen
                    </Typography>
                    <Typography
                      variant="h5"
                      fontWeight="bold"
                      sx={{
                        mt: 0.5,
                        fontSize: { xs: "1.1rem", sm: "1.5rem" },
                        wordBreak: "break-word",
                      }}
                    >
                      Citizen Screens
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "rgba(255,255,255,0.92)" }}
                    >
                      Home carousels — order, hide, covers & spotlight
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  background:
                    "linear-gradient(135deg, #c2410c 0%, #ea580c 100%)",
                  color: "#fff",
                  borderRadius: 3,
                  boxShadow: "0 16px 40px rgba(15,23,42,0.25)",
                  cursor: "pointer",
                  "&:hover": {
                    boxShadow: "0 20px 50px rgba(15,23,42,0.35)",
                    transform: "translateY(-4px)",
                  },
                  transition: "all 0.3s",
                }}
                onClick={() => navigate("/mytownscreens")}
              >
                <CardContent sx={gradientCardContentSx}>
                  <Box sx={gradientCardIconSx}>
                    <GridViewIcon sx={{ fontSize: 36 }} />
                  </Box>
                  <Box sx={gradientCardTextSx}>
                    <Typography
                      variant="overline"
                      sx={{ color: "rgba(255,255,255,0.85)" }}
                    >
                      Locsy Citizen
                    </Typography>
                    <Typography
                      variant="h5"
                      fontWeight="bold"
                      sx={{
                        mt: 0.5,
                        fontSize: { xs: "1.1rem", sm: "1.5rem" },
                        wordBreak: "break-word",
                      }}
                    >
                      MyTown Screens
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "rgba(255,255,255,0.92)" }}
                    >
                      Category cards — images, labels, order & hide
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  background:
                    "linear-gradient(135deg, #115e59 0%, #0d9488 100%)",
                  color: "#fff",
                  borderRadius: 3,
                  boxShadow: "0 16px 40px rgba(15,23,42,0.25)",
                  cursor: "pointer",
                  "&:hover": {
                    boxShadow: "0 20px 50px rgba(15,23,42,0.35)",
                    transform: "translateY(-4px)",
                  },
                  transition: "all 0.3s",
                }}
                onClick={() => navigate("/content/online-course-sales")}
              >
                <CardContent sx={gradientCardContentSx}>
                  <Box sx={gradientCardIconSx}>
                    <SchoolIcon sx={{ fontSize: 36 }} />
                  </Box>
                  <Box sx={gradientCardTextSx}>
                    <Typography
                      variant="overline"
                      sx={{ color: "rgba(255,255,255,0.85)" }}
                    >
                      Learn Zone
                    </Typography>
                    <Typography
                      variant="h5"
                      fontWeight="bold"
                      sx={{
                        mt: 0.5,
                        fontSize: { xs: "1.1rem", sm: "1.5rem" },
                        wordBreak: "break-word",
                      }}
                    >
                      Online Course Sales
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "rgba(255,255,255,0.92)" }}
                    >
                      KNLS sales totals · Mitra listed for later settlement
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  background:
                    "linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)",
                  color: "#fff",
                  borderRadius: 3,
                  boxShadow: "0 16px 40px rgba(15,23,42,0.25)",
                  cursor: "pointer",
                  "&:hover": {
                    boxShadow: "0 20px 50px rgba(15,23,42,0.35)",
                    transform: "translateY(-4px)",
                  },
                  transition: "all 0.3s",
                }}
                onClick={() => navigate("/catalog-templates")}
              >
                <CardContent sx={gradientCardContentSx}>
                  <Box sx={gradientCardIconSx}>
                    <Inventory2OutlinedIcon sx={{ fontSize: 36 }} />
                  </Box>
                  <Box>
                    <Typography variant="overline" sx={{ color: "rgba(255,255,255,0.85)" }}>
                      Smart Bill defaults
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" sx={{ mt: 0.5, fontSize: { xs: "1.1rem", sm: "1.5rem" }, wordBreak: "break-word" }}>
                      Catalog templates
                    </Typography>
                    <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.92)" }}>
                      Kirana, net centre, bakery starter lists by Citizen category
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  background:
                    "linear-gradient(135deg, #1d4ed8 0%, #3b82f6 100%)",
                  color: "#fff",
                  borderRadius: 3,
                  boxShadow: "0 16px 40px rgba(15,23,42,0.25)",
                  cursor: "pointer",
                  '&:hover': {
                    boxShadow: '0 20px 50px rgba(15,23,42,0.35)',
                    transform: 'translateY(-4px)',
                  },
                  transition: 'all 0.3s',
                }}
                onClick={() => navigate('/business-admin/verification')}
              >
                <CardContent sx={gradientCardContentSx}>
                  <Box sx={gradientCardIconSx}>
                    <VerifiedUserIcon sx={{ fontSize: 36 }} />
                    </Box>
                    <Box sx={gradientCardTextSx}>
                      <Typography
                        variant="overline"
                      sx={{ color: 'rgba(255,255,255,0.8)' }}
                    >
                      Business Admin Verification
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" sx={{ mt: 0.5, fontSize: { xs: "1.1rem", sm: "1.5rem" }, wordBreak: "break-word" }}>
                      Verify businesses
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: 'rgba(255,255,255,0.9)' }}
                    >
                      Review Mitra profiles and grant the Verified badge
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  background:
                    "linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)",
                  color: "#fff",
                  borderRadius: 3,
                  boxShadow: "0 16px 40px rgba(15,23,42,0.25)",
                  cursor: "pointer",
                  "&:hover": {
                    boxShadow: "0 20px 50px rgba(15,23,42,0.35)",
                    transform: "translateY(-4px)",
                  },
                  transition: "all 0.3s",
                }}
                onClick={() => navigate("/business-admin/register")}
              >
                <CardContent sx={gradientCardContentSx}>
                  <Box sx={gradientCardIconSx}>
                    <PersonAddIcon sx={{ fontSize: 36 }} />
                  </Box>
                  <Box sx={gradientCardTextSx}>
                    <Typography
                      variant="overline"
                      sx={{ color: "rgba(255,255,255,0.8)" }}
                    >
                      Admin Approvals
                    </Typography>
                    <Typography
                      variant="h5"
                      fontWeight="bold"
                      sx={{
                        mt: 0.5,
                        fontSize: { xs: "1.1rem", sm: "1.5rem" },
                        wordBreak: "break-word",
                      }}
                    >
                      vyapar registration form
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "rgba(255,255,255,0.9)" }}
                    >
                      Assisted Mitra / Vyapar registration when OTP is unavailable
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "#fff",
                  borderRadius: 3,
                  boxShadow: "0 16px 40px rgba(15,23,42,0.25)",
                  cursor: "pointer",
                  "&:hover": {
                    boxShadow: "0 20px 50px rgba(15,23,42,0.35)",
                    transform: "translateY(-4px)",
                  },
                  transition: "all 0.3s",
                }}
                onClick={() => navigate("/business-category-index")}
              >
                <CardContent sx={gradientCardContentSx}>
                  <Box sx={gradientCardIconSx}>
                    <StoreIcon sx={{ fontSize: 36 }} />
                  </Box>
                  <Box>
                    <Typography variant="overline" sx={{ color: "rgba(255,255,255,0.85)" }}>
                      Registration & My Town
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" sx={{ mt: 0.5, fontSize: { xs: "1.1rem", sm: "1.5rem" }, wordBreak: "break-word" }}>
                      Shops & Services Index
                    </Typography>
                    <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.92)" }}>
                      Add flex printers, kirana, xerox — manage city shop categories
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  background: "linear-gradient(135deg, #c2410c 0%, #ea580c 100%)",
                  color: "#fff",
                  borderRadius: 3,
                  boxShadow: "0 16px 40px rgba(15,23,42,0.25)",
                  cursor: "pointer",
                  "&:hover": {
                    boxShadow: "0 20px 50px rgba(15,23,42,0.35)",
                    transform: "translateY(-4px)",
                  },
                  transition: "all 0.3s",
                }}
                onClick={() => navigate("/mitra-token-board-settings")}
              >
                <CardContent sx={gradientCardContentSx}>
                  <Box sx={gradientCardIconSx}>
                    <TuneIcon sx={{ fontSize: 36 }} />
                  </Box>
                  <Box>
                    <Typography variant="overline" sx={{ color: "rgba(255,255,255,0.85)" }}>
                      Token Board defaults
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" sx={{ mt: 0.5, fontSize: { xs: "1.1rem", sm: "1.5rem" }, wordBreak: "break-word" }}>
                      Mitra Token Board Settings
                    </Typography>
                    <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.92)" }}>
                      Prepaid / postpaid and board defaults by category — no coding
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            {isLocalInternalHost() && (
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  background:
                    "linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)",
                  color: "#fff",
                  borderRadius: 3,
                  boxShadow: "0 16px 40px rgba(15,23,42,0.25)",
                  cursor: "pointer",
                  "&:hover": {
                    boxShadow: "0 20px 50px rgba(15,23,42,0.35)",
                    transform: "translateY(-4px)",
                  },
                  transition: "all 0.3s",
                }}
                onClick={() => navigate("/tables")}
              >
                <CardContent sx={gradientCardContentSx}>
                  <Box sx={gradientCardIconSx}>
                    <TableChartIcon sx={{ fontSize: 36 }} />
                    </Box>
                    <Box sx={gradientCardTextSx}>
                      <Typography
                        variant="overline"
                      sx={{ color: "rgba(255,255,255,0.8)" }}
                    >
                      My Tables
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" sx={{ mt: 0.5, fontSize: { xs: "1.1rem", sm: "1.5rem" }, wordBreak: "break-word" }}>
                      Database Explorer
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "rgba(255,255,255,0.9)" }}
                    >
                      View and manage all database tables
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            )}
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  background:
                    "linear-gradient(135deg, #25D366 0%, #128C7E 100%)",
                  color: "#fff",
                  borderRadius: 3,
                  boxShadow: "0 16px 40px rgba(15,23,42,0.25)",
                  cursor: "pointer",
                  "&:hover": {
                    boxShadow: "0 20px 50px rgba(15,23,42,0.35)",
                    transform: "translateY(-4px)",
                  },
                  transition: "all 0.3s",
                }}
                onClick={() => navigate("/bills/whatsapp-config")}
              >
                <CardContent sx={gradientCardContentSx}>
                  <Box sx={gradientCardIconSx}>
                    <WhatsAppIcon sx={{ fontSize: 36 }} />
                    </Box>
                    <Box sx={gradientCardTextSx}>
                      <Typography
                        variant="overline"
                      sx={{ color: "rgba(255,255,255,0.8)" }}
                    >
                      WhatsApp Config
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" sx={{ mt: 0.5, fontSize: { xs: "1.1rem", sm: "1.5rem" }, wordBreak: "break-word" }}>
                      Message Customization
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "rgba(255,255,255,0.9)" }}
                    >
                      Customize all WhatsApp messages - Bills, Work Status, Job
                      Alerts & more
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  background:
                    "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                  color: "#fff",
                  borderRadius: 3,
                  boxShadow: "0 16px 40px rgba(15,23,42,0.25)",
                  cursor: "pointer",
                  "&:hover": {
                    boxShadow: "0 20px 50px rgba(15,23,42,0.35)",
                    transform: "translateY(-4px)",
                  },
                  transition: "all 0.3s",
                }}
                onClick={() => navigate("/users/analytics")}
              >
                <CardContent sx={gradientCardContentSx}>
                  <Box sx={gradientCardIconSx}>
                    <AssessmentIcon sx={{ fontSize: 36 }} />
                    </Box>
                    <Box sx={gradientCardTextSx}>
                      <Typography
                        variant="overline"
                      sx={{ color: "rgba(255,255,255,0.8)" }}
                    >
                      User Analytics
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" sx={{ mt: 0.5, fontSize: { xs: "1.1rem", sm: "1.5rem" }, wordBreak: "break-word" }}>
                      Login Statistics
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "rgba(255,255,255,0.9)" }}
                    >
                      Track users, logins & services used
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  background:
                    "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                  color: "#fff",
                  borderRadius: 3,
                  boxShadow: "0 16px 40px rgba(15,23,42,0.25)",
                  cursor: "pointer",
                  "&:hover": {
                    boxShadow: "0 20px 50px rgba(15,23,42,0.35)",
                    transform: "translateY(-4px)",
                  },
                  transition: "all 0.3s",
                }}
                onClick={() => navigate("/job-alerts/enquiries")}
              >
                <CardContent sx={gradientCardContentSx}>
                  <Box sx={gradientCardIconSx}>
                    <AssignmentIcon sx={{ fontSize: 36 }} />
                    </Box>
                    <Box sx={gradientCardTextSx}>
                      <Typography
                        variant="overline"
                      sx={{ color: "rgba(255,255,255,0.8)" }}
                    >
                      Job Alerts Enquiries
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" sx={{ mt: 0.5, fontSize: { xs: "1.1rem", sm: "1.5rem" }, wordBreak: "break-word" }}>
                      JOBALETSNQRY
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "rgba(255,255,255,0.9)" }}
                    >
                      View and manage all job alerts registration enquiries
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  background:
                    "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  color: "#fff",
                  borderRadius: 3,
                  boxShadow: "0 16px 40px rgba(15,23,42,0.25)",
                  cursor: "pointer",
                  "&:hover": {
                    boxShadow: "0 20px 50px rgba(15,23,42,0.35)",
                    transform: "translateY(-4px)",
                  },
                  transition: "all 0.3s",
                }}
                onClick={() => navigate("/dashboard/students/courses")}
              >
                <CardContent sx={gradientCardContentSx}>
                  <Box sx={gradientCardIconSx}>
                    <LibraryBooksIcon sx={{ fontSize: 36 }} />
                    </Box>
                    <Box sx={gradientCardTextSx}>
                      <Typography
                        variant="overline"
                      sx={{ color: "rgba(255,255,255,0.8)" }}
                    >
                      Course Database
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" sx={{ mt: 0.5, fontSize: { xs: "1.1rem", sm: "1.5rem" }, wordBreak: "break-word" }}>
                      Update Course Details
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "rgba(255,255,255,0.9)" }}
                    >
                      Open course management to edit DB course details and syllabus
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            {canAccessRoute("/training-programs") && (
              <Grid item xs={12} sm={6} md={4}>
                <Card
                  sx={{
                    background:
                      "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
                    color: "#fff",
                    borderRadius: 3,
                    boxShadow: "0 16px 40px rgba(15,23,42,0.25)",
                    cursor: "pointer",
                    "&:hover": {
                      boxShadow: "0 20px 50px rgba(15,23,42,0.35)",
                      transform: "translateY(-4px)",
                    },
                    transition: "all 0.3s",
                  }}
                  onClick={() => navigate("/dashboard/training-programs")}
                >
                  <CardContent sx={gradientCardContentSx}>
                    <Box sx={gradientCardIconSx}>
                      <SchoolIcon sx={{ fontSize: 36 }} />
                    </Box>
                    <Box sx={gradientCardTextSx}>
                      <Typography
                        variant="overline"
                        sx={{ color: "rgba(255,255,255,0.8)" }}
                      >
                        Training Programs
                      </Typography>
                      <Typography variant="h5" fontWeight="bold" sx={{ mt: 0.5, fontSize: { xs: "1.1rem", sm: "1.5rem" }, wordBreak: "break-word" }}>
                        Update Programs DB
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "rgba(255,255,255,0.9)" }}
                      >
                        Add, edit, and remove items shown on /training-programs
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* URL Manager Card - Only for admins */}
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  background:
                    "linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)",
                  color: "#fff",
                  borderRadius: 3,
                  boxShadow: "0 16px 40px rgba(15,23,42,0.25)",
                  cursor: "pointer",
                  "&:hover": {
                    boxShadow: "0 20px 50px rgba(15,23,42,0.35)",
                    transform: "translateY(-4px)",
                  },
                  transition: "all 0.3s",
                }}
                onClick={() => navigate("/url-manager")}
              >
                <CardContent sx={gradientCardContentSx}>
                  <Box sx={gradientCardIconSx}>
                    <VisibilityIcon sx={{ fontSize: 36 }} />
                    </Box>
                    <Box sx={gradientCardTextSx}>
                      <Typography
                        variant="overline"
                      sx={{ color: "rgba(255,255,255,0.8)" }}
                    >
                      URL Manager
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" sx={{ mt: 0.5, fontSize: { xs: "1.1rem", sm: "1.5rem" }, wordBreak: "break-word" }}>
                      Manage URLs & Links
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "rgba(255,255,255,0.9)" }}
                    >
                      Search and manage important URLs and credentials
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* LD2HD Upscaler - Free Phase 1 */}
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  background:
                    "linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)",
                  color: "#fff",
                  borderRadius: 3,
                  boxShadow: "0 16px 40px rgba(15,23,42,0.25)",
                  cursor: "pointer",
                  "&:hover": {
                    boxShadow: "0 20px 50px rgba(15,23,42,0.35)",
                    transform: "translateY(-4px)",
                  },
                  transition: "all 0.3s",
                }}
                onClick={() => navigate("/ld2hd")}
              >
                <CardContent sx={gradientCardContentSx}>
                  <Box sx={gradientCardIconSx}>
                    <HighQualityIcon sx={{ fontSize: 36 }} />
                    </Box>
                    <Box sx={gradientCardTextSx}>
                      <Typography
                        variant="overline"
                      sx={{ color: "rgba(255,255,255,0.8)" }}
                    >
                      LD2HD
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" sx={{ mt: 0.5, fontSize: { xs: "1.1rem", sm: "1.5rem" }, wordBreak: "break-word" }}>
                      Image Upscaler
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "rgba(255,255,255,0.9)" }}
                    >
                      4x, 8x, 16x — 100% free, no premium limits
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Bill Statistics Section - Only for non-operators */}
      {!isOperator && (
        <Box sx={{ mt: { xs: 3, md: 4 } }}>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            fontWeight="bold"
            sx={sectionTitleSx}
          >
            Bill Statistics
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Service charges summary (excluding challana amounts)
          </Typography>

          {billStats.loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 4,
              }}
            >
              <CircularProgress size={24} />
              <Typography variant="body2" sx={{ ml: 2 }}>
                Loading bill statistics...
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={GRID_SPACING}>
              {/* Today */}
              <Grid item xs={12} sm={6} md={4}>
                <Card
                  sx={{
                    bgcolor: "primary.light",
                    color: "primary.contrastText",
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, sm: 2.5 }, "&:last-child": { pb: { xs: 2, sm: 2.5 } } }}>
                    <Box sx={billStatRowSx}>
                      <Box sx={billStatTextSx}>
                        <Typography
                          variant="body2"
                          sx={{ opacity: 0.9, mb: 1 }}
                        >
                          Today
                        </Typography>
                        <Typography
                          variant="h4"
                          component="div"
                          fontWeight="bold"
                          sx={{ mb: 1, fontSize: { xs: "1.35rem", sm: "2rem" } }}
                        >
                          {billStats.today.count} Bills
                        </Typography>
                        <Typography
                          variant="h5"
                          component="div"
                          fontWeight="bold"
                          sx={{ fontSize: { xs: "1.1rem", sm: "1.5rem" }, wordBreak: "break-word" }}
                        >
                          ₹
                          {billStats.today.total.toLocaleString("en-IN", {
                            maximumFractionDigits: 0,
                          })}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ opacity: 0.8, mt: 0.5, display: "block" }}
                        >
                          Service Charges Only
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          bgcolor: "rgba(255, 255, 255, 0.2)",
                          borderRadius: 2,
                          p: { xs: 1, sm: 1.5 },
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          "& .MuiSvgIcon-root": { fontSize: { xs: 32, sm: 40 } },
                        }}
                      >
                        <TodayIcon sx={{ fontSize: 40 }} />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* This Week */}
              <Grid item xs={12} sm={6} md={4}>
                <Card
                  sx={{
                    bgcolor: "success.light",
                    color: "success.contrastText",
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, sm: 2.5 }, "&:last-child": { pb: { xs: 2, sm: 2.5 } } }}>
                    <Box sx={billStatRowSx}>
                      <Box sx={billStatTextSx}>
                        <Typography
                          variant="body2"
                          sx={{ opacity: 0.9, mb: 1 }}
                        >
                          This Week
                        </Typography>
                        <Typography
                          variant="h4"
                          component="div"
                          fontWeight="bold"
                          sx={{ mb: 1, fontSize: { xs: "1.35rem", sm: "2rem" } }}
                        >
                          {billStats.weekly.count} Bills
                        </Typography>
                        <Typography
                          variant="h5"
                          component="div"
                          fontWeight="bold"
                          sx={{ fontSize: { xs: "1.1rem", sm: "1.5rem" }, wordBreak: "break-word" }}
                        >
                          ₹
                          {billStats.weekly.total.toLocaleString("en-IN", {
                            maximumFractionDigits: 0,
                          })}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ opacity: 0.8, mt: 0.5, display: "block" }}
                        >
                          Service Charges Only
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          bgcolor: "rgba(255, 255, 255, 0.2)",
                          borderRadius: 2,
                          p: { xs: 1, sm: 1.5 },
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          "& .MuiSvgIcon-root": { fontSize: { xs: 32, sm: 40 } },
                        }}
                      >
                        <CalendarTodayIcon sx={{ fontSize: 40 }} />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* This Month */}
              <Grid item xs={12} sm={6} md={4}>
                <Card
                  sx={{ bgcolor: "info.light", color: "info.contrastText" }}
                >
                  <CardContent sx={{ p: { xs: 2, sm: 2.5 }, "&:last-child": { pb: { xs: 2, sm: 2.5 } } }}>
                    <Box sx={billStatRowSx}>
                      <Box sx={billStatTextSx}>
                        <Typography
                          variant="body2"
                          sx={{ opacity: 0.9, mb: 1 }}
                        >
                          This Month
                        </Typography>
                        <Typography
                          variant="h4"
                          component="div"
                          fontWeight="bold"
                          sx={{ mb: 1, fontSize: { xs: "1.35rem", sm: "2rem" } }}
                        >
                          {billStats.monthly.count} Bills
                        </Typography>
                        <Typography
                          variant="h5"
                          component="div"
                          fontWeight="bold"
                          sx={{ fontSize: { xs: "1.1rem", sm: "1.5rem" }, wordBreak: "break-word" }}
                        >
                          ₹
                          {billStats.monthly.total.toLocaleString("en-IN", {
                            maximumFractionDigits: 0,
                          })}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ opacity: 0.8, mt: 0.5, display: "block" }}
                        >
                          Service Charges Only
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          bgcolor: "rgba(255, 255, 255, 0.2)",
                          borderRadius: 2,
                          p: { xs: 1, sm: 1.5 },
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          "& .MuiSvgIcon-root": { fontSize: { xs: 32, sm: 40 } },
                        }}
                      >
                        <DateRangeIcon sx={{ fontSize: 40 }} />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </Box>
      )}

      {isLoading && !isOperator && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 2,
            mt: 2,
          }}
        >
          <CircularProgress size={24} />
          <Typography variant="body2" sx={{ ml: 2 }}>
            Loading statistics...
          </Typography>
        </Box>
      )}

      {/* Quick Actions - Show only for non-operators or operators with permissions */}
      {(!isOperator ||
        (isOperator && user?.permissions && user.permissions.length > 0)) && (
        <Box
          sx={{
            mt: 4,
            p: { xs: 2, sm: 2.5, md: 3 },
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}
        >
          <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}>
            Quick Actions
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Quick access to frequently used modules.
          </Typography>
          <Grid container spacing={GRID_SPACING} sx={{ mt: 1 }}>
            {canAccessRoute("/bills/create") && (
              <Grid item xs={12} sm={6} md={4}>
                <Card
                  sx={{
                    p: { xs: 2, sm: 2.5 },
                    height: "100%",
                    cursor: "pointer",
                    border: "1px solid #e0e0e0",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    "&:hover": {
                      bgcolor: "primary.main",
                      color: "white",
                      boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s",
                  }}
                  onClick={() => navigate("/bills/create")}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                    <ReceiptLongIcon sx={{ fontSize: 28 }} />
                    <Typography variant="body1" fontWeight="600">
                      Smart Bill (QR)
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            )}
            {canAccessRoute("/business-admin/register") && (
              <Grid item xs={12} sm={6} md={4}>
                <Card
                  sx={{
                    p: { xs: 2, sm: 2.5 },
                    height: "100%",
                    cursor: "pointer",
                    border: "1px solid #e0e0e0",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    "&:hover": {
                      bgcolor: "teal",
                      color: "white",
                      boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s",
                  }}
                  onClick={() => navigate("/business-admin/register")}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                    <PersonAddIcon sx={{ fontSize: 28 }} />
                    <Box>
                      <Typography variant="body1" fontWeight="600">
                        vyapar registration form
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.7 }}>
                        Assisted registration when OTP is unavailable
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            )}
            {(user?.role === "app_admin" || user?.role === "super_admin") && (
              <Grid item xs={12} sm={6} md={4}>
                <Card
                  sx={{
                    p: { xs: 2, sm: 2.5 },
                    height: "100%",
                    cursor: "pointer",
                    border: "1px solid #e0e0e0",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    "&:hover": {
                      bgcolor: "success.main",
                      color: "white",
                      boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s",
                  }}
                  onClick={() => navigate("/mitrascreens")}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                    <PhoneAndroidIcon sx={{ fontSize: 28 }} />
                    <Box>
                      <Typography variant="body1" fontWeight="600">
                        Mitra Screens
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.7 }}>
                        Citizen Interaction & Internal Operations cards
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            )}
            {(user?.role === "app_admin" || user?.role === "super_admin") && (
              <Grid item xs={12} sm={6} md={4}>
                <Card
                  sx={{
                    p: { xs: 2, sm: 2.5 },
                    height: "100%",
                    cursor: "pointer",
                    border: "1px solid #e0e0e0",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    "&:hover": {
                      bgcolor: "primary.main",
                      color: "white",
                      boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s",
                  }}
                  onClick={() => navigate("/loksyhomescreen_controls")}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                    <PhoneAndroidIcon sx={{ fontSize: 28 }} />
                    <Box>
                      <Typography variant="body1" fontWeight="600">
                        Citizen Screens
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.7 }}>
                        Home carousels — order, hide, covers
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            )}
            {(user?.role === "app_admin" || user?.role === "super_admin") && (
              <Grid item xs={12} sm={6} md={4}>
                <Card
                  sx={{
                    p: { xs: 2, sm: 2.5 },
                    height: "100%",
                    cursor: "pointer",
                    border: "1px solid #e0e0e0",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    "&:hover": {
                      bgcolor: "warning.main",
                      color: "white",
                      boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s",
                  }}
                  onClick={() => navigate("/mytownscreens")}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                    <GridViewIcon sx={{ fontSize: 28 }} />
                    <Box>
                      <Typography variant="body1" fontWeight="600">
                        MyTown Screens
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.7 }}>
                        Category cards — images, order & hide
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            )}
            {(user?.role === "app_admin" || user?.role === "super_admin") && (
              <Grid item xs={12} sm={6} md={4}>
                <Card
                  sx={{
                    p: { xs: 2, sm: 2.5 },
                    height: "100%",
                    cursor: "pointer",
                    border: "1px solid #e0e0e0",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    "&:hover": {
                      bgcolor: "primary.main",
                      color: "white",
                      boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s",
                  }}
                  onClick={() => navigate("/content/coupon-flow")}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                    <CouponIcon sx={{ fontSize: 28 }} />
                    <Box>
                      <Typography variant="body1" fontWeight="600">
                        Coupon Flow
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.7 }}>
                        DigiDrop — coupons per ad type
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            )}
            {(user?.role === "app_admin" ||
              user?.role === "super_admin" ||
              user?.role === "app_operator") && (
              <Grid item xs={12} sm={6} md={4}>
                <Card
                  sx={{
                    p: { xs: 2, sm: 2.5 },
                    height: "100%",
                    cursor: "pointer",
                    border: "1px solid #e0e0e0",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    "&:hover": {
                      bgcolor: "success.dark",
                      color: "white",
                      boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s",
                  }}
                  onClick={() => navigate("/content/online-course-sales")}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                    <SchoolIcon sx={{ fontSize: 28 }} />
                    <Box>
                      <Typography variant="body1" fontWeight="600">
                        Online Course Sales
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.7 }}>
                        KNLS totals · Mitra settle later
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            )}
            {canAccessRoute("/content/lucky-wheel") && (
              <Grid item xs={12} sm={6} md={4}>
                <Card
                  sx={{
                    p: { xs: 2, sm: 2.5 },
                    height: "100%",
                    cursor: "pointer",
                    border: "1px solid #e0e0e0",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    "&:hover": {
                      bgcolor: "warning.main",
                      color: "white",
                      boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s",
                  }}
                  onClick={() => navigate("/content/lucky-wheel")}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                    <CouponIcon sx={{ fontSize: 28 }} />
                    <Box>
                      <Typography variant="body1" fontWeight="600">
                        Locsy Spin
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.7 }}>
                        Lucky wheel odds · listing / shop / bill
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            )}
            {canAccessRoute("/bills/cart-model") && (
              <Grid item xs={12} sm={6} md={4}>
                <Card
                  sx={{
                    p: { xs: 2, sm: 2.5 },
                    height: "100%",
                    cursor: "pointer",
                    border: "1px solid #e0e0e0",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    "&:hover": {
                      bgcolor: "success.main",
                      color: "white",
                      boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s",
                  }}
                  onClick={() => navigate("/bills/cart-model")}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                    <ShoppingCartCheckoutIcon sx={{ fontSize: 28 }} />
                    <Box>
                      <Typography variant="body1" fontWeight="600">
                        Cart Model Bill
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.7 }}>
                        Fast cart-style billing
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            )}
            {canAccessRoute("/bills/view") && (
              <Grid item xs={12} sm={6} md={4}>
                <Card
                  sx={{
                    p: { xs: 2, sm: 2.5 },
                    height: "100%",
                    cursor: "pointer",
                    border: "1px solid #e0e0e0",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    "&:hover": {
                      bgcolor: "info.main",
                      color: "white",
                      boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s",
                  }}
                  onClick={() => navigate("/bills/view")}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                    <VisibilityIcon sx={{ fontSize: 28 }} />
                    <Box>
                      <Typography variant="body1" fontWeight="600">
                        View Bills
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.7 }}>
                        View Bills
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            )}
            {canAccessRoute("/bills/advance-bills") && (
              <Grid item xs={12} sm={6} md={4}>
                <Card
                  sx={{
                    p: { xs: 2, sm: 2.5 },
                    height: "100%",
                    cursor: "pointer",
                    border: "1px solid #e0e0e0",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    "&:hover": {
                      bgcolor: "primary.main",
                      color: "white",
                      boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s",
                  }}
                  onClick={() => navigate("/bills/advance-bills")}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                    <WalletIcon sx={{ fontSize: 28 }} />
                    <Box>
                      <Typography variant="body1" fontWeight="600">
                        Advance Bills
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.7 }}>
                        Advance amount bills
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            )}
            {canAccessRoute("/bills/pending-works") && (
              <Grid item xs={12} sm={6} md={4}>
                <Card
                  sx={{
                    p: { xs: 2, sm: 2.5 },
                    height: "100%",
                    cursor: "pointer",
                    border: "1px solid #e0e0e0",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    "&:hover": {
                      bgcolor: "warning.main",
                      color: "white",
                      boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s",
                  }}
                  onClick={() => navigate("/bills/pending-works")}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                    <PendingActionsIcon sx={{ fontSize: 28 }} />
                    <Box>
                      <Typography variant="body1" fontWeight="600">
                        Pending Works
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.7 }}>
                        Manage work status updates
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            )}
            {canAccessRoute("/bills/enquiry-form") && (
              <Grid item xs={12} sm={6} md={4}>
                <Card
                  sx={{
                    p: { xs: 2, sm: 2.5 },
                    height: "100%",
                    cursor: "pointer",
                    border: "1px solid #e0e0e0",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    "&:hover": {
                      bgcolor: "success.main",
                      color: "white",
                      boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s",
                  }}
                  onClick={() => navigate("/bills/enquiry-form")}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                    <QuestionAnswerIcon sx={{ fontSize: 28 }} />
                    <Box>
                      <Typography variant="body1" fontWeight="600">
                        Enquiries
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.7 }}>
                        Register callback when available
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            )}
            {canAccessRoute("/bills/counter-sales-datewise") && (
              <Grid item xs={12} sm={6} md={4}>
                <Card
                  sx={{
                    p: { xs: 2, sm: 2.5 },
                    height: "100%",
                    cursor: "pointer",
                    border: "1px solid #e0e0e0",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    "&:hover": {
                      bgcolor: "secondary.main",
                      color: "white",
                      boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s",
                  }}
                  onClick={() => navigate("/bills/counter-sales-datewise")}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                    <CalendarMonthIcon sx={{ fontSize: 28 }} />
                    <Box>
                      <Typography variant="body1" fontWeight="600">
                        Counter Sales (Dates)
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.7 }}>
                        Scrollable date-wise model
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            )}
            {canAccessRoute("/bills/services") && (
              <Grid item xs={12} sm={6} md={4}>
                <Card
                  sx={{
                    p: { xs: 2, sm: 2.5 },
                    height: "100%",
                    cursor: "pointer",
                    border: "1px solid #e0e0e0",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    "&:hover": {
                      bgcolor: "success.main",
                      color: "white",
                      boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s",
                  }}
                  onClick={() => navigate("/bills/services")}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                    <SettingsIcon sx={{ fontSize: 28 }} />
                    <Typography variant="body1" fontWeight="600">
                      Manage Services & Bills
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            )}
            {canAccessRoute("/application-chat") && (
              <Grid item xs={12} sm={6} md={4}>
                <Card
                  sx={{
                    p: { xs: 2, sm: 2.5 },
                    height: "100%",
                    cursor: "pointer",
                    border: "1px solid #e0e0e0",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    "&:hover": {
                      bgcolor: "secondary.main",
                      color: "white",
                      boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s",
                  }}
                  onClick={() => navigate("/application-chat")}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                    <ForumIcon sx={{ fontSize: 28 }} />
                    <Typography variant="body1" fontWeight="600">
                      Application Chat
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            )}
            {canAccessRoute("/services") && (
              <Grid item xs={12} sm={6} md={4}>
                <Card
                  sx={{
                    p: { xs: 2, sm: 2.5 },
                    height: "100%",
                    cursor: "pointer",
                    border: "1px solid #e0e0e0",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "white",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
                      boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s",
                  }}
                  onClick={() => navigate("/services")}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                    <VerifiedUserIcon sx={{ fontSize: 28 }} />
                    <Typography variant="body1" fontWeight="600">
                      KNLS Services
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            )}
            {canAccessRoute("/dashboard/training-programs") && (
              <Grid item xs={12} sm={6} md={4}>
                <Card
                  sx={{
                    p: { xs: 2, sm: 2.5 },
                    height: "100%",
                    cursor: "pointer",
                    border: "1px solid #e0e0e0",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    background:
                      "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
                    color: "white",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #1d4ed8 0%, #2563eb 100%)",
                      boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s",
                  }}
                  onClick={() => navigate("/dashboard/training-programs")}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                    <SchoolIcon sx={{ fontSize: 28 }} />
                    <Box>
                      <Typography variant="body1" fontWeight="600">
                        Edit Training Programs
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Manage /training-programs content
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            )}
            {(user?.role === "app_admin" ||
              user?.role === "super_admin" ||
              user?.role === "net_center_admin") && (
              <Grid item xs={12} sm={6} md={4}>
                <Card
                  sx={{
                    p: { xs: 2, sm: 2.5 },
                    height: "100%",
                    cursor: "pointer",
                    border: "1px solid #e0e0e0",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    background:
                      "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                    color: "white",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #d97706 0%, #f59e0b 100%)",
                      boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s",
                  }}
                  onClick={() => navigate("/dashboard/students")}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                    <SchoolIcon sx={{ fontSize: 28 }} />
                    <Box>
                      <Typography variant="body1" fontWeight="600">
                        Students Management
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Register & manage students
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            )}
            {canAccessRoute("/job-alerts") && (
              <Grid item xs={12} sm={6} md={4}>
                <Card
                  sx={{
                    p: { xs: 2, sm: 2.5 },
                    height: "100%",
                    cursor: "pointer",
                    border: "1px solid #e0e0e0",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    "&:hover": {
                      bgcolor: "secondary.main",
                      color: "white",
                      boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s",
                  }}
                  onClick={() => navigate("/job-alerts")}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                    <WorkIcon sx={{ fontSize: 28 }} />
                    <Typography variant="body1" fontWeight="600">
                      Job Alerts Management
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            )}
            {canAccessRoute("/coupons") && (
              <Grid item xs={12} sm={6} md={4}>
                <Card
                  sx={{
                    p: { xs: 2, sm: 2.5 },
                    height: "100%",
                    cursor: "pointer",
                    border: "1px solid #e0e0e0",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    "&:hover": {
                      bgcolor: "warning.main",
                      color: "white",
                      boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s",
                  }}
                  onClick={() => navigate("/coupons")}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                    <CouponIcon sx={{ fontSize: 28 }} />
                    <Typography variant="body1" fontWeight="600">
                      Coupons Management
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            )}
            {canAccessRoute("/online-works") && (
              <Grid item xs={12} sm={6} md={4}>
                <Card
                  sx={{
                    p: { xs: 2, sm: 2.5 },
                    height: "100%",
                    cursor: "pointer",
                    border: "1px solid #e0e0e0",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    "&:hover": {
                      bgcolor: "error.main",
                      color: "white",
                      boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s",
                  }}
                  onClick={() => navigate("/online-works")}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                    <BusinessCenterIcon sx={{ fontSize: 28 }} />
                    <Typography variant="body1" fontWeight="600">
                      Online Works
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            )}
            {canAccessRoute("/backup") && (
              <Grid item xs={12} sm={6} md={4}>
                <Card
                  sx={{
                    p: { xs: 2, sm: 2.5 },
                    height: "100%",
                    cursor: "pointer",
                    border: "1px solid #e0e0e0",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    "&:hover": {
                      bgcolor: "info.main",
                      color: "white",
                      boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s",
                  }}
                  onClick={() => navigate("/backup")}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                    <CloudDownloadIcon sx={{ fontSize: 28 }} />
                    <Typography variant="body1" fontWeight="600">
                      Backup & Export
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            )}
            {(user?.role === "app_admin" || user?.role === "super_admin") && (
              <Grid item xs={12} sm={6} md={4}>
                <Card
                  sx={{
                    p: { xs: 2, sm: 2.5 },
                    height: "100%",
                    cursor: "pointer",
                    border: "1px solid #e0e0e0",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    background:
                      "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                    color: "white",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)",
                      boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s",
                  }}
                  onClick={() => navigate("/users/analytics")}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                    <AssessmentIcon sx={{ fontSize: 28 }} />
                    <Typography variant="body1" fontWeight="600">
                      User Analytics
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            )}
          </Grid>
        </Box>
      )}

      <Box sx={{ mt: 3, p: { xs: 1.5, sm: 2 }, bgcolor: "warning.light", borderRadius: 1 }}>
        <Typography variant="body2" color="warning.dark" sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
          <strong>Security Notice:</strong> This system is monitored. All
          actions are logged for security and compliance purposes.
        </Typography>
      </Box>
    </Box>
  );
};

export default Dashboard;
